"use client";

import CaretDownIcon from "public/next-public/common/icons/caret-solid-down.svg";
import React, { useEffect, useRef, useState } from "react";
import styles from "./menuDropdown.module.scss";

const MenuDropdown: React.FC<MenuDropdownProps> = ({
  label,
  options,
  selectedValue,
  onSelect,
  children,
  disabled = false,
  align = "left",
  color = "default",
  compact = false,
  caretSize = 12,
  closeOnSelect = true,
  id,
  ariaLabel,
  className,
  menuClassName,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleSelect = (value: string): void => {
    onSelect?.(value);
    if (closeOnSelect) setOpen(false);
  };

  const rootClasses = [styles.MenuDropdown, open ? styles.open : "", compact ? styles.compact : "", className || ""]
    .filter(Boolean)
    .join(" ");
  const menuClasses = [styles.menu, align === "right" ? styles.alignRight : "", menuClassName || ""]
    .filter(Boolean)
    .join(" ");
  const triggerClasses = [styles.trigger, color === "brand" ? styles.triggerBrand : "", disabled ? styles.disabled : ""]
    .filter(Boolean)
    .join(" ");
  // Custom children are not a menu (e.g. a search field + checkbox list), so they must not
  // sit inside role="menu"; only the built-in `options` list renders true menu items.
  const hasCustomContent = children != null;

  return (
    <div className={rootClasses} ref={containerRef}>
      <button
        type="button"
        id={id}
        className={triggerClasses}
        aria-haspopup={hasCustomContent ? "dialog" : "menu"}
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        {label}
        <span className={styles.caret} aria-hidden="true">
          <CaretDownIcon width={caretSize} height={caretSize} />
        </span>
      </button>

      {open && (
        <div className={menuClasses} role={hasCustomContent ? undefined : "menu"}>
          {children
            ? children
            : options?.map((option) => {
                const isSelected = option.value === selectedValue;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isSelected}
                    className={[styles.option, isSelected ? styles.selected : ""].filter(Boolean).join(" ")}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
        </div>
      )}
    </div>
  );
};

export default MenuDropdown;

export interface MenuDropdownOption {
  label: string;
  value: string;
}

interface MenuDropdownProps {
  label: React.ReactNode;
  options?: MenuDropdownOption[];
  selectedValue?: string;
  onSelect?: (value: string) => void;
  /** When set, overrides the `options` list. */
  children?: React.ReactNode;
  disabled?: boolean;
  align?: "left" | "right";
  /** Trigger treatment. "default" is the neutral label; "brand" renders the label in
   *  the brand text color with the active-icon caret. */
  color?: "default" | "brand";
  /** The pre-refresh menu: padded, 14px options, no dividers. Existing consumers only. */
  compact?: boolean;
  /** Caret width and height, in px. */
  caretSize?: 12 | 16;
  closeOnSelect?: boolean;
  id?: string;
  ariaLabel?: string;
  className?: string;
  menuClassName?: string;
}
